import { State, MainMessageType, RedirectAction } from '../../../types/state';
import { Listing } from '../../../types/listing/listing';
import { setMainMessage, clearMessage } from '../system/set-main-message';
import { postData, batchDeleteImagesFromStorage } from '../../../api/glAxios'
import { updateListingQuery, createNewListingQuery } from '../../../api/glQueries'
import { publishListing } from '../listingEntry/publish-listing';
import { exportListing } from '../listingEntry/export-listing';
import { setAlertMessage } from '../../../redux/actions/system/set-alert-message';
import { AlertMessagingType } from '../../../types/state';
import { Config } from '../../../types/config/config';
import { currentListingLoaded} from '../../../redux/actions/listingEntry/load-current-listing';
import { setReloadListingId } from '../system/set-reload-listing-id';
import { updateSavePending } from './update-pending-operation';
import { resetPagedListings } from '../pagedListings/load-listings-paged';
import { clearMIQData } from '../miq/clear-miq-data';
import { checkAssignFlag } from '../../../utils/miq/remove-assign-flag';
import { showMIQExportError, showMIQExportSuccess } from '../miq/set-miq-export-message';
import { getAssignedListingsCount } from '../miq/get-assigned-listings-count';
import { push } from 'connected-react-router';
import { authContext } from '../../../adalConfig';
import { TeamMember } from '../../../types/team/teamMember';
import { setIsDuplicatingListing } from '../system/set-is-duplicating-listing';
import { unpublishListing, UnpublishType } from './unpublish-listing';
import { setMIQStatus } from '../miq/set-miq-status';
import moment from 'moment';
import { Space } from '../../../types/listing/space'
import { Option } from '../../../types/common/option';
import defaultListing from '../../../api/defaults/listing';
import parseDate from '../../../utils/listing/convert-listing-date';
import { findClassByRange } from '../../../utils/config/epcRating-options';
import { findSpecificationFields } from '../../../utils/config/specifications-fields';
import { standardSpecifications } from '../../../api/defaults/specs-standard';
import { GLFile } from '../../../types/listing/file';

const saveData = (listing:Listing, config:Config, regionId?: string) => {
    let data:any;
    if(listing.demoGraphics)
        {
            const temp = listing.demoGraphics.demoGraphicsDetails;
            if(listing.demoGraphics.interval1 === null)
            {
                temp.forEach(detail => {
                    detail.value1 = null;
                });
            }
    
            if(listing.demoGraphics.interval2 === null)
            {
                temp.forEach(detail => {
                    detail.value2 = null;
                });
            }
    
            if(listing.demoGraphics.interval3 === null)
            {
                temp.forEach(detail => {
                    detail.value3 = null;
                });
            }
        }

        if (listing.id){
            const query = updateListingQuery(listing, config, regionId);
            data = postData(query)
        } else {    
            const query = createNewListingQuery(listing, config, regionId)
            data = postData(query);
        }
        
        return data;
}

export enum SaveType {
    SAVE = "SAVE",
    PUBLISH = "PUBLISH",
    EXPORT = "EXPORT",
    ASSIGN = "ASSIGN",
    DUPLICATE = "DUPLICATE", // Save the current listing before duplicating. On success begin a 2nd save with type = SAVEDUPLICATE
    SAVEDUPLICATE = "SAVEDUPLICATE", // Save a duplicate of the listing by calling CreateListing with the same data
    UNPUBLISH_FROM_MIQ = "UNPUBLISH",
}

// thunks
export const saveListing = (listing:Listing, saveType:SaveType, config:Config) => (dispatch: Function, getState: () => State) => {


    const processPendingImageDeletions = () => {
        if (typeof window === 'undefined') {
            return;
        }

        const globalScope: any = window as any;
        const pendingDeletions: GLFile[] = Array.isArray(globalScope.__pendingImageDeletions)
            ? globalScope.__pendingImageDeletions
            : [];

        if (pendingDeletions.length === 0) {
            return;
        }

        const uniqueDeletions = pendingDeletions.filter((file, index, list) =>
            file.id && list.findIndex(f => f.id === file.id) === index);

        batchDeleteImagesFromStorage(uniqueDeletions)
            .then(() => {
                globalScope.__pendingImageDeletions = [];
            })
            .catch((error) => {
                console.error('Batch delete failed', error);
                globalScope.__pendingImageDeletions = [];
            });
    }

    // this helps us ensure only one save operation is occurring at a time.
    if(getState().entry.pendingOperations.savePending === true && saveType !== "SAVEDUPLICATE"){
        return;
    }

    if(config.listingStatuses && config.listingStatuses.show && config.featureFlags && config.featureFlags.showRemainingListingTypeInStatus) {
        // if the listing status selected by user is not in the config, set it to empty
        const sId = config.listingStatuses.options.findIndex((option: Option) => option.value === listing.listingStatus);
        if(sId < 0) {
            listing.listingStatus = "";
        }
    }

    if(config.fgee && config.fgee.show && config.classFgee && config.classFgee.options && listing.fgee !== undefined && listing.fgee !== null
        && (listing.classFgee === undefined || listing.classFgee === null || listing.classFgee === '')) {
        listing.classFgee = findClassByRange(listing.fgee.toString(), config.classFgee.options as any);
    }

    if(config.hwb && config.hwb.show && config.classHwb && config.classHwb.options && listing.hwb !== undefined && listing.hwb !== null
        && (listing.classHwb === undefined || listing.classHwb === null || listing.classHwb === '')) {
        listing.classHwb = findClassByRange(listing.hwb.toString(), config.classHwb.options as any);
    }
    if(config.featureFlags.specificationsRules && config.featureFlags.specificationsRules.length > 0) {
        const specificationFields = findSpecificationFields(config, listing);
        const fieldsToConsider = Object.keys(specificationFields || {}).map((f) => specificationFields[f].properties && specificationFields[f].properties.name ? specificationFields[f].properties.name : f);
        if(fieldsToConsider && fieldsToConsider.length > 0) {
            const defaultSpecs = config.specifications && config.specifications.defaults ? config.specifications.defaults : standardSpecifications;
            Object.keys(listing.specifications).forEach(k => {
                if(!fieldsToConsider.includes(k)) {
                    listing.specifications[k] = defaultSpecs[k];
                }
            });
        }
    }
    
    if (!listing.id && !listing.owner) {
        const currentUser = authContext.getCachedUser();
        listing.owner = currentUser.userName;
        const ownerUser: TeamMember = { 
            teamMemberId: "", 
            firstName: currentUser.profile.given_name, 
            lastName: currentUser.profile.family_name, 
            id: currentUser.userName, 
            email: currentUser.userName };
        if (!listing.users) {
            listing.users = [];
        }
        listing.users.push(ownerUser);
    }

    // Check if the currect authContext user exists in the listing's user data, if not, add user to the listing before save
    if (listing.users && listing.users.length > 0){
        const currentUser = authContext.getCachedUser();
        let matchFound = false;
        listing.users.forEach((user: TeamMember) => { // do an email match
            if (user.id === currentUser.userName || user.email === currentUser.userName || user.email === currentUser.profile.upn || user.id === currentUser.profile.unique_name){
                matchFound = true;
            }
        })
        
        if (!matchFound){
            const ownerUser: TeamMember = { 
                teamMemberId: "", 
                firstName: currentUser.profile.given_name, 
                lastName: currentUser.profile.family_name, 
                id: currentUser.userName, 
                email: currentUser.userName 
            };
            listing.users.push(ownerUser);
        }
    }

    if (getState().miq.pendingAddEditRecord) {
        listing.importedData = JSON.stringify(getState().miq.pendingAddEditRecord);
    }

    // set save pending = true to avoid double operations occurring
    dispatch(updateSavePending(true));  

    let messageToUser = "Saving Listing...";
    if(saveType === SaveType.PUBLISH){
        messageToUser = "Publishing Listing...";
    }else if(saveType === SaveType.EXPORT){
        messageToUser = "Exporting...";
    }else if (saveType === SaveType.UNPUBLISH_FROM_MIQ){
        messageToUser = "Unpublishing Listing...";
    }else if (saveType === SaveType.SAVEDUPLICATE){
        messageToUser = "Duplicating Listing...";
    }
    dispatch(setMainMessage({ show: true, type: MainMessageType.SAVING, message: messageToUser }));

    // check for MIQ assignment to turn off the assigned flag on save
    let assignFlagChange:boolean = false;
    if(saveType !== SaveType.ASSIGN && checkAssignFlag(listing)){
        assignFlagChange = true;
        if(listing && listing.listingAssignment){
            listing.listingAssignment.assignmentFlag = false;
        }
    }

    // if the property is toggled as "Send to Toimitilat" and availablelFrom value is null then populating
    // the value with current date while publishing a record for Finland
    if( listing.syndicationFlag && getState().mapping.countryCode === "fi" && saveType === SaveType.PUBLISH)
    {
        if(!listing.availableFrom)
        {
            listing.availableFrom = moment().local().format("YYYY-MM-DDT11:00:00");
        }
        
        if(listing.spaces && listing.spaces.length > 0)
        {
            listing.spaces.forEach((space: Space) => {
                if (!space.availableFrom) {
                    space.availableFrom = moment().local().format("YYYY-MM-DDT11:00:00");
                }
            })
        }
    }
    const regionId = getState().mapping.selectedCountry;

    if(config.leasedInfo && config.leasedInfo.show) {
        
        if(listing.listingType === 'leased') {
            if(!listing.leasedInfo) {
                // if the leasedInfo object is not set, then we need to set it to the default listing leasedInfo
                listing.leasedInfo = defaultListing.leasedInfo;
            } else {
                // set the fileds in the leasedInfo object to null if they are not set
                listing.leasedInfo = {
                    rentPerAnnum: !!listing.leasedInfo.rentPerAnnum ? listing.leasedInfo.rentPerAnnum : null,
                    leasedDate: !!listing.leasedInfo.leasedDate ? parseDate(listing.leasedInfo.leasedDate) : null,
                    leasedDuration: !!listing.leasedInfo.leasedDuration ? listing.leasedInfo.leasedDuration : null,
                    leasedPrice: !!listing.leasedInfo.leasedPrice ? listing.leasedInfo.leasedPrice : null,
                    displayPrice: !!listing.leasedInfo.displayPrice ? listing.leasedInfo.displayPrice : false
                };
            }
        } else {
            // if the listing type is not leased, then we need to remove the leased info from the listing
            // this is a workaround for the fact that we don't have a way to remove the leased info from the UI
            // so we just remove it from the listing object before saving
            delete listing.leasedInfo;
        }
        
    }

    if(config.soldInfo && config.soldInfo.show) {

        if(listing.listingType === 'sold') {
            if(!listing.soldInfo) {
                // if the soldInfo object is not set, then we need to set it to the default listing soldInfo
                listing.soldInfo = defaultListing.soldInfo;
            } else {
                // set the fileds in the soldInfo object to null if they are not set
                listing.soldInfo = {
                    soldDate: !!listing.soldInfo.soldDate ? parseDate(listing.soldInfo.soldDate) : null,
                    soldPrice: !!listing.soldInfo.soldPrice ? listing.soldInfo.soldPrice : null,
                    displayPrice: !!listing.soldInfo.displayPrice ? listing.soldInfo.displayPrice : false
                };
            }
        } else {
            // if the listing type is not sold, then we need to remove the sold info from the listing
            // this is a workaround for the fact that we don't have a way to remove the sold info from the UI
            // so we just remove it from the listing object before saving
            delete listing.soldInfo;
        }

    }

    saveData(listing, config, regionId).then((result: any) => {

        // if data returns a create listing or update listing object, it worked & push to home      
        if (result && result.data && (result.data.createListing || result.data.updateListing)){

            processPendingImageDeletions();
            resetPagedListings(dispatch, false);   // this will force a refresh on the listing page  
            dispatch(clearMIQData());     // ensure any miq data screens are reset

            // This flag is used by load-current-listing to indicate that the user clicked the duplicate button after we save changes to the original listing.
            dispatch(setIsDuplicatingListing(saveType === SaveType.DUPLICATE));

            if (result.data.createListing){
                clearMessage(dispatch);
                listing.id = result.data.createListing.id;     
                if (saveType !== SaveType.ASSIGN) {
                    dispatch(currentListingLoaded(listing));
                }
            }

            if (saveType === SaveType.PUBLISH) {
                if(assignFlagChange) { dispatch(getAssignedListingsCount) };
                dispatch(publishListing(listing));
            } else if (saveType === SaveType.EXPORT) {
                dispatch(exportListing(listing, config));
                dispatch(updateSavePending(false));
                clearMessage(dispatch); // this probably should be moved to the export thunk
            } else if (saveType === SaveType.ASSIGN) {
                clearMessage(dispatch);
                dispatch(setAlertMessage({ show: true, message: 'Listing Assigned Successfully', type: AlertMessagingType.NOTICE, allowClose: true }));
                dispatch(showMIQExportSuccess("Listing has been assigned to","the user(s) you have selected."));
                dispatch(updateSavePending(false));
            } else if (saveType === SaveType.DUPLICATE) {
                dispatch(setAlertMessage({ show: true, message: 'A copy has been made of your listing.  Be sure to save after making any necessary updates.', type: AlertMessagingType.NOTICE, allowClose: false }));
                dispatch(push(`/le/${listing.id}`));
                dispatch(setReloadListingId(listing.id));
                dispatch(updateSavePending(false));
            } else if (saveType === SaveType.SAVEDUPLICATE) {
                dispatch(setAlertMessage({ show: true, message: 'A copy of your listing has been saved.', type: AlertMessagingType.NOTICE, allowClose: true }));
                dispatch(push(`/le/${listing.id}`));
                dispatch(setReloadListingId(listing.id));
                dispatch(updateSavePending(false));
            } else if (saveType === SaveType.UNPUBLISH_FROM_MIQ) {
                dispatch(unpublishListing(listing, UnpublishType.MIQ_NEW));
            } else {
                clearMessage(dispatch);
                if(assignFlagChange) { dispatch(getAssignedListingsCount) };
                dispatch(setAlertMessage({ show: true, message: 'Listing Saved Successfully', type: AlertMessagingType.NOTICE, allowClose: true }));
                
                const saveAction:RedirectAction = getState().system.saveAction;
                if(saveAction && saveAction.goto){
                    dispatch(showMIQExportSuccess("Save Successful", "Your MIQ Listing has been created in Global Listings"));
                    dispatch(push(saveAction.goto));
                }else{
                    dispatch(push(`/le/${listing.id}`));
                    dispatch(setReloadListingId(listing.id));
                }
                dispatch(updateSavePending(false));
            }
        } else{
            // error state
            clearMessage(dispatch);
            dispatch(updateSavePending(false));
            
            if (saveType === SaveType.ASSIGN) {
                dispatch(setAlertMessage({show: true, message: "There was an Error while saving.", type: AlertMessagingType.ERROR, allowClose: true}));
                dispatch(showMIQExportError("Error","There was an error assigning this listing."));
            } else if (saveType === SaveType.UNPUBLISH_FROM_MIQ) {
                dispatch(setAlertMessage({show: true, message: "Unable to automatically save a copy of your listing. Please use the create button to resolve the issues and unpublish the listing.", type: AlertMessagingType.ERROR, allowClose: true}));
                dispatch(setMIQStatus({loading: false, error: false, message: ""}));
            } else {
                mapHighlightsObject();
                dispatch(currentListingLoaded(listing));    // ensure the UI is refreshed with the copy we tried to save, otherwise the user loses anything they've done
                let errorMessage = "There was an Error while saving.";
                if (saveType === SaveType.DUPLICATE) {
                    errorMessage = "There was an error while duplicating your listing.  Be sure to save after making any necessary updates.";
                }
                dispatch(setAlertMessage({show: true, message: errorMessage, type: AlertMessagingType.ERROR, allowClose: true}));
            }
        }
    })

    // Re-Construction Highlights object to it's original structure.
    // As it is converted to DB Model while saving/updating the listing. 
    const mapHighlightsObject = () => {
        const listingHighlights: any[] = [];

        listing.highlights.forEach((scrubbedHighlight: any) => {
            const { order, text, cultureCode, miqId } = scrubbedHighlight;

            if (!listingHighlights[order]) {
                listingHighlights[order] = {
                    value: [],
                };
            }

            listingHighlights[order].value.push({
                text,
                cultureCode,
                miqId,
            });
        });

        listing.highlights = listingHighlights;
    }
}